import { AIInput } from '@modules/ai-conversation/components/AIInput/AIInput';
import { Box, Paper, Popper, styled } from '@mui/material';
import { PopoverVirtualElement } from '@mui/material';
import { Editor, type Range, posToDOMRect } from '@tiptap/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AiChatMessage } from 'src/@types/ai';
import { useConfirmationModal } from 'src/components/ConfirmationModal';
import { useLocales } from 'src/locales';
import { AIMessages } from 'src/sections/@dashboard/ai/Chat/AIConversation/AIMessages/AIMessages';
import { scrollParent } from 'src/utils/scrollparent';
import useResizeObserver from 'use-resize-observer';
import { getEditorContainer } from '../../utils/getEditorContainer';
import { AiPromptActions } from '../AiPromptActions';
import { useAiPrompt } from './useAiPrompt';

export const AiPrompt = ({
  editor,
  onClose,
  deleteRange,
}: {
  editor: Editor;
  onClose: () => void;
  deleteRange?: Range;
}) => {
  const prompt = useAiPrompt(editor);
  const { inputValue, setInputValue, think } = prompt.control((s) => ({
    inputValue: s.inputValue,
    setInputValue: s.setInputValue,
    think: s.think,
  }));
  const {
    handleSendChatMessage,
    configs,
    status: responseStatus,
    stop,
  } = prompt;
  const { position, container, responseContainerHeight } = configs;
  const { translate } = useLocales();
  const inputRef = useRef<HTMLInputElement>(null);
  const isFirstRender = useRef(true);
  const boxRef = useRef<HTMLDivElement>(null);
  const { confirm } = useConfirmationModal();
  const [anchorEl, setAnchorEl] = useState<PopoverVirtualElement | null>(null);

  const handleClose = useCallback(() => {
    setInputValue('');
    onClose();
  }, [setInputValue, onClose]);

  const { width } = useResizeObserver<HTMLElement>({
    ref: editor.view.dom,
  });

  useEffect(() => {
    if (!isFirstRender.current) return;

    setInputValue('');
    inputRef.current?.focus();
    isFirstRender.current = false;
  }, [setInputValue]);

  useEffect(() => {
    editor.commands.setSelectionHighlight();

    return () => {
      const s = prompt.selectionRef.current;
      editor
        .chain()
        .setTextSelection({
          from: s.from,
          to: s.to,
        })
        .unsetSelectionHighlight()
        .run();
    };
  }, [editor, prompt.selectionRef]);

  useEffect(() => {
    const { messages } = prompt;
    const isEmpty = inputValue.length === 0 && messages.length === 0;

    const handleKeyDown = (event: KeyboardEvent) => {
      const closingKeys = ['Escape', 'Backspace', 'Delete'];
      if (closingKeys.includes(event.key) && isEmpty) {
        handleClose();
        editor.commands.focus();
      }
    };
    const handleClickOutside = async (event: MouseEvent) => {
      const isOutside = !boxRef.current?.contains(event.target as Node);
      if (isOutside && event.isTrusted) {
        if (isEmpty) {
          handleClose();
          return;
        }
        const isConfirmationClick =
          event.target instanceof HTMLButtonElement &&
          event.target.type === 'button';
        if (!isEmpty && !isConfirmationClick) {
          event.stopPropagation();
          event.preventDefault();
          const canClose = await confirm({
            title: translate('shared.confirmation.are_you_sure_title'),
            message: translate('shared.confirmation.youll_lost_your_changes'),
          });
          if (canClose) handleClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [
    prompt,
    inputValue.length,
    translate,
    confirm,
    handleClose,
    editor.commands.focus,
  ]);

  useEffect(() => {
    let lastY = 0;
    let animationFrameId = 0;
    const computeAnchorEl = () => {
      const selection = prompt.selectionRef.current;
      const rects = posToDOMRect(editor.view, selection.from, selection.to);
      if (rects.y === lastY) {
        animationFrameId = requestAnimationFrame(computeAnchorEl);
        return;
      }
      const editorRects = editor.view.dom.getBoundingClientRect();
      const scrollableParent = scrollParent(editor.view.dom);

      let top = rects.top;
      if (position === 'fixed' && scrollableParent instanceof HTMLElement) {
        const margin = 5;
        top = editorRects.top + scrollableParent.scrollTop - margin;
      }

      lastY = rects.y;
      setAnchorEl({
        nodeType: 1,
        getBoundingClientRect: () => {
          return {
            ...rects,
            top: top,
            left: editorRects.left,
          };
        },
      });
      animationFrameId = requestAnimationFrame(computeAnchorEl);
    };

    animationFrameId = requestAnimationFrame(computeAnchorEl);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [editor, prompt.selectionRef, position]);

  const modifiers = useMemo(
    () => [
      {
        name: 'preventOverflow',
        enabled: true,
        options: {
          altAxis: true,
          boundary: 'clippingParents',
          rootBoundary: 'viewport',
          tether: true,
          padding: 15,
        },
      },
      {
        name: 'flip',
        enabled: true,
        options: {
          altBoundary: true,
          boundary: 'clippingParents',
          rootBoundary: 'viewport',
        },
      },
    ],
    [],
  );

  const messagesList = useMemo(
    () =>
      (responseStatus === 'submitted'
        ? [
            ...prompt.messages,
            {
              id: 'loading',
              role: 'assistant',
              content: '',
              status: 'loading',
              think: think,
            },
          ]
        : prompt.messages) as AiChatMessage[],
    [responseStatus, prompt.messages, think],
  );

  return (
    <Popper
      placement="top-start"
      modifiers={modifiers}
      sx={{
        zIndex: 'modal',
      }}
      anchorEl={anchorEl}
      open={!!anchorEl}
      ref={boxRef}
      container={() => {
        if (container === 'editor') {
          return getEditorContainer(editor);
        }
        return null;
      }}
    >
      <StyledAiPromptContainer
        sx={{
          //! NO FIXED MARGIN
          marginLeft: '-18px',
          //! WTF is 32?
          width: width! + 32,
          //! NO ARBITRARY SIZE VALUES
          maxWidth: 800,
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <StyledPaper
          elevation={1}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <StyledAiResponseContainer>
            {prompt.messages.length > 0 && (
              <StyledMessagesContainer
                sx={{
                  height: responseContainerHeight
                    ? responseContainerHeight
                    : '300px',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <AIMessages
                  editor={editor}
                  store={prompt.control}
                  messages={messagesList}
                />
              </StyledMessagesContainer>
            )}
            <AIInput
              disableButtons={responseStatus === 'submitted'}
              sendMessage={handleSendChatMessage}
              control={prompt.control}
              placeholder={translate('shared.ai_prompt.placeholder')}
              showAudioAnswer={false}
              stop={stop}
            />
          </StyledAiResponseContainer>
        </StyledPaper>
        <AiPromptActions
          prompt={prompt}
          onClose={handleClose}
          editor={editor}
          deleteRange={deleteRange}
        />
      </StyledAiPromptContainer>
    </Popper>
  );
};

const StyledAiPromptContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  zIndex: 'tooltip',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  py: 0,
  boxShadow: theme.customShadows.dropdown,
}));

const StyledAiResponseContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
  gap: theme.spacing(1),
}));

const StyledMessagesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  backgroundColor: theme.palette.background.default,
  marginBottom: theme.spacing(1),
}));
